import { ref, onMounted, onUnmounted } from 'vue'
import { gsap } from 'gsap'

export function useWhalePointAnimations() {
  const isAnimating = ref(false)

  // Create floating WP notification
  function createFloatingWP(amount: number, targetElement?: HTMLElement) {
    const container = targetElement || document.body

    // Create floating element
    const floatingEl = document.createElement('div')
    floatingEl.className = 'wp-floating-notification'

    // Create content div safely
    const contentDiv = document.createElement('div')
    contentDiv.className = 'wp-float-content'

    // Create icon element
    const iconEl = document.createElement('i')
    iconEl.className = 'fa-solid fa-whale'

    // Create text node
    const textNode = document.createTextNode(`+${formatWP(amount)} WP`)

    // Assemble elements
    contentDiv.appendChild(iconEl)
    contentDiv.appendChild(textNode)
    floatingEl.appendChild(contentDiv)

    // Style the element
    floatingEl.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 9999;
      pointer-events: none;
      font-family: system-ui, -apple-system, sans-serif;
    `

    const content = floatingEl.querySelector('.wp-float-content') as HTMLElement
    if (content) {
      content.style.cssText = `
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 20px;
        background: linear-gradient(135deg, #324ac6, #627bfa);
        color: white;
        border-radius: 8px;
        font-weight: 700;
        font-size: 16px;
        box-shadow: 0 8px 32px rgba(50, 74, 198, 0.4);
        border: 2px solid rgba(255, 255, 255, 0.2);
      `
    }

    container.appendChild(floatingEl)

    // Animate
    const tl = gsap.timeline({
      onComplete: () => {
        container.removeChild(floatingEl)
        isAnimating.value = false
      }
    })

    tl.fromTo(floatingEl,
      {
        opacity: 0,
        scale: 0.5,
        y: 20
      },
      {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.4,
        ease: 'back.out(1.7)'
      }
    )
    .to(floatingEl, {
      opacity: 0,
      y: -50,
      scale: 0.8,
      duration: 0.6,
      ease: 'power2.in',
      delay: 1.5
    })

    isAnimating.value = true
    return tl
  }

  // Animate WP counter increment
  function animateWPCounter(element: HTMLElement, fromValue: number, toValue: number) {
    const obj = { value: fromValue }

    return gsap.to(obj, {
      value: toValue,
      duration: 1,
      ease: 'power2.out',
      onUpdate: () => {
        element.textContent = formatWP(Math.round(obj.value))
      }
    })
  }

  // Simple WP format function
  function formatWP(wp: number): string {
    if (wp >= 1000000) return `${(wp / 1000000).toFixed(1)}M`
    if (wp >= 1000) return `${(wp / 1000).toFixed(1)}K`
    return wp.toString()
  }

  // Listen for WP earned events
  let eventListener: ((event: CustomEvent) => void) | null = null

  onMounted(() => {
    eventListener = (event: CustomEvent) => {
      const { amount } = event.detail
      createFloatingWP(amount)
    }

    window.addEventListener('wp-earned', eventListener as EventListener)
  })

  onUnmounted(() => {
    if (eventListener) {
      window.removeEventListener('wp-earned', eventListener as EventListener)
    }
  })

  return {
    isAnimating,
    createFloatingWP,
    animateWPCounter,
    formatWP
  }
}

// Global style injection for floating notifications
let stylesInjected = false

export function injectWPAnimationStyles() {
  if (stylesInjected || typeof document === 'undefined') return

  const style = document.createElement('style')
  style.textContent = `
    .wp-floating-notification {
      animation: wp-float 2.5s ease-out forwards;
    }

    @keyframes wp-float {
      0% {
        opacity: 0;
        transform: translate(-50%, -50%) scale(0.5) translateY(20px);
      }
      25% {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1) translateY(0px);
      }
      75% {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1) translateY(0px);
      }
      100% {
        opacity: 0;
        transform: translate(-50%, -50%) scale(0.8) translateY(-50px);
      }
    }
  `

  document.head.appendChild(style)
  stylesInjected = true
}